import React, {useState} from 'react'
import {createPortal} from 'react-dom'
import {useHealthCheck, HealthStatus} from './HealthCheckProvider'

interface HealthStatusIndicatorProps {
  showDetails?: boolean
  compact?: boolean
  className?: string
}

export const HealthStatusIndicator: React.FC<HealthStatusIndicatorProps> = ({
  showDetails = false,
  compact = false,
  className = '',
}) => {
  const {healthStatus, isOverallHealthy, checkHealth, isLoading} = useHealthCheck()
  const [showModal, setShowModal] = useState(false)

  if (Object.keys(healthStatus).length === 0) {
    return null
  }

  // Get unhealthy services for detailed view
  const unhealthyServices = Object.values(healthStatus).filter(
    (status) => status.status === 'unhealthy'
  )
  const hasIssues = unhealthyServices.length > 0

  const getStatusIcon = (status: HealthStatus['status']) => {
    switch (status) {
      case 'healthy':
        return <i className='fas fa-check-circle text-success' />
      case 'unhealthy':
        return <i className='fas fa-times-circle text-danger' />
      case 'checking':
        return <i className='fas fa-spinner fa-spin text-warning' />
      default:
        return <i className='fas fa-question-circle text-muted' />
    }
  }

  const getStatusText = (status: HealthStatus['status']) => {
    switch (status) {
      case 'healthy':
        return 'Healthy'
      case 'unhealthy':
        return 'Unhealthy'
      case 'checking':
        return 'Checking...'
      case 'unknown':
        return 'Not checked'
      default:
        return 'Unknown'
    }
  }

  const formatResponseTime = (responseTime?: number) => {
    if (!responseTime) return ''
    return `${responseTime}ms`
  }

  const formatLastCheck = (lastCheck: Date | null) => {
    if (!lastCheck) return 'Never'
    const now = new Date()
    const diff = now.getTime() - lastCheck.getTime()
    const seconds = Math.floor(diff / 1000)

    if (seconds < 60) return `${seconds}s ago`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
    return `${Math.floor(seconds / 3600)}h ago`
  }

  // Compact indicator
  if (compact) {
    return (
      <div className={`health-status-compact ${className}`}>
        <button
          className='btn btn-sm btn-outline-secondary'
          onClick={() => setShowModal(true)}
          title={isOverallHealthy ? 'All services healthy' : 'Service issues detected'}
          disabled={isLoading}
        >
          {isLoading ? (
            <i className='fas fa-spinner fa-spin' />
          ) : (
            getStatusIcon(isOverallHealthy ? 'healthy' : 'unhealthy')
          )}
          <span className='ms-1'>
            {isOverallHealthy ? 'All OK' : `${unhealthyServices.length} Issues`}
          </span>
        </button>

        {/* Health Status Modal */}
        {showModal &&
          createPortal(
            <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
              <div className='modal-dialog modal-dialog-centered modal-lg ob-dialog'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title'>Service Health Status</h5>
                    <button
                      type='button'
                      className='btn-close'
                      onClick={() => setShowModal(false)}
                    />
                  </div>
                  <div className='modal-body'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <span>
                        Overall Status: {isOverallHealthy ? 'Healthy' : 'Issues Detected'}
                      </span>
                      <button
                        className='btn btn-sm btn-outline-primary'
                        onClick={() => checkHealth()}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <i className='fas fa-spinner fa-spin' />
                        ) : (
                          <i className='fas fa-sync-alt' />
                        )}
                        <span className='ms-1'>Refresh</span>
                      </button>
                    </div>

                    <div className='table-responsive'>
                      <table className='table table-sm'>
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Status</th>
                            <th>Response Time</th>
                            <th>Last Check</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(healthStatus).map(([key, status]) => (
                            <tr key={key}>
                              <td>
                                <strong>{status.name}</strong>
                                <br />
                                <small className='text-muted'>{status.url}</small>
                              </td>
                              <td>
                                <span className='d-flex align-items-center'>
                                  {getStatusIcon(status.status)}
                                  <span className='ms-1'>{getStatusText(status.status)}</span>
                                </span>
                              </td>
                              <td>
                                <small>{formatResponseTime(status.responseTime)}</small>
                              </td>
                              <td>
                                <small>{formatLastCheck(status.lastCheck)}</small>
                              </td>
                              <td>
                                {status.error && (
                                  <small className='text-danger' title={status.error}>
                                    {status.error.length > 50
                                      ? `${status.error.substring(0, 50)}...`
                                      : status.error}
                                  </small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button
                      type='button'
                      className='btn btn-secondary'
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    )
  }

  // Detailed indicator
  return (
    <div className={`health-status-detailed ${className}`}>
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h6 className='mb-0'>
            <i className='fas fa-heartbeat me-2' />
            Service Health Status
          </h6>
          <div>
            <button
              className='btn btn-sm btn-outline-primary'
              onClick={() => checkHealth()}
              disabled={isLoading}
            >
              {isLoading ? (
                <i className='fas fa-spinner fa-spin' />
              ) : (
                <i className='fas fa-sync-alt' />
              )}
              <span className='ms-1'>Refresh</span>
            </button>
          </div>
        </div>
        <div className='card-body'>
          {/* Show minimal view when everything is healthy */}
          {!hasIssues && isOverallHealthy ? (
            <div className='text-center py-4'>
              <div className='d-flex align-items-center justify-content-center mb-3'>
                <i className='fas fa-check-circle text-success fs-1 me-3'></i>
                <div>
                  <h5 className='mb-1 text-success'>All Systems Up and Running</h5>
                  <p className='text-muted mb-0'>All external services are operational</p>
                </div>
              </div>
              <small className='text-muted'>Last updated: {new Date().toLocaleString()}</small>
            </div>
          ) : (
            /* Show detailed view when there are issues */
            <div>
              <div className='alert alert-warning mb-4'>
                <i className='fas fa-exclamation-triangle me-2'></i>
                <strong>Service Issues Detected:</strong> {unhealthyServices.length} service
                {unhealthyServices.length !== 1 ? 's' : ''}{' '}
                {unhealthyServices.length === 1 ? 'is' : 'are'} currently unavailable.
              </div>

              <div className='row'>
                {Object.entries(healthStatus).map(([key, status]) => (
                  <div key={key} className='col-md-6 col-lg-4 mb-3'>
                    <div className='border rounded p-3'>
                      <div className='d-flex justify-content-between align-items-start mb-2'>
                        <h6 className='mb-0'>{status.name}</h6>
                        {getStatusIcon(status.status)}
                      </div>
                      <small className='text-muted d-block mb-1'>{status.url}</small>
                      <div className='d-flex justify-content-between align-items-center'>
                        <span
                          className={`badge ${
                            status.status === 'healthy'
                              ? 'bg-success'
                              : status.status === 'unhealthy'
                              ? 'bg-danger'
                              : 'bg-warning'
                          }`}
                        >
                          {getStatusText(status.status)}
                        </span>
                        {status.responseTime && (
                          <small className='text-muted'>
                            {formatResponseTime(status.responseTime)}
                          </small>
                        )}
                      </div>
                      {status.error && (
                        <small className='text-danger d-block mt-1' title={status.error}>
                          {status.error}
                        </small>
                      )}
                      <small className='text-muted d-block mt-1'>
                        Last check: {formatLastCheck(status.lastCheck)}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showDetails && (
            <div className='mt-3 pt-3 border-top'>
              <h6>Environment Information</h6>
              <div className='row'>
                <div className='col-md-6'>
                  <small className='text-muted'>
                    <strong>Environment:</strong> {process.env.NODE_ENV}
                  </small>
                </div>
                <div className='col-md-6'>
                  <small className='text-muted'>
                    <strong>Host:</strong> {window.location.hostname}
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
